import { useEffect, useMemo, useState } from "react";
import { Button, Input, message, Table, Tag, Tooltip, Typography } from "antd";
import Icon from "@ant-design/icons";
import { Icons } from "../../assets/icons";
import { QueryOperation } from "../../code/search-query/search-query";
import {
  CalendarInvitation,
  EventParticipantRole,
  MAX_PAGE_SIZE,
  PlatformUser,
} from "../../repository/data.default";
import {
  deleteCalendarInvitation,
  getCalendarInvitations,
  getPlatformUsers,
} from "../../repository/data.repository";
import { nameOf } from "../../utils/typescript";
import { getProblemMessage } from "../../utils/http";
import {
  mapInvitation,
  ParticipantItem,
  participantRole,
  participantTagColor,
} from "./calendarEventForm";
import TargetListFrame, { frameEmptyText } from "./TargetListFrame";

// Two-line attendee row: 8px + 20px name + 16px detail + 8px padding + 1px border.
const ROW_HEIGHT = 53;
// Rows visible before the table scrolls: new event (staged) vs a saved event's participants.
const STAGED_VISIBLE_ROWS = 3;
const PERSISTED_VISIBLE_ROWS = 4;

export interface EventParticipantsPanelProps {
  // When set, the panel shows the event's persisted participants (searchable). When absent (a
  // not-yet-created event), it renders the in-memory `staged` list instead. Both use the same table.
  readonly eventUid?: string;
  readonly staged: ParticipantItem[];
  readonly editable: boolean;
  // Bump to force a refetch of the persisted list (e.g. after the parent invites someone immediately).
  readonly refreshKey?: number;
  readonly onRemoveStaged: (index: number) => void;
}

const fullName = (u: PlatformUser): string =>
  [u.firstName, u.lastName].filter(Boolean).join(" ") || u.userName;

// Invitations only carry the username — enrich them with the invitee's full name, department and role
// name (one users request by uid) so saved events read exactly like the add-event table. Falls back to
// the plain invitation rows if the users lookup fails.
function loadParticipants(eventUid: string): Promise<ParticipantItem[]> {
  return getCalendarInvitations({
    searchQuery: {
      conditions: [
        {
          field: nameOf<CalendarInvitation>("eventUid"),
          operation: QueryOperation.Equal,
          values: [eventUid],
        },
      ],
    },
    pageNumber: 0,
    pageSize: MAX_PAGE_SIZE,
    sortBy: nameOf<CalendarInvitation>("createdOn"),
    sortDescending: true,
  }).then((result) => {
    const items = result.data.map(mapInvitation);
    const uids = Array.from(new Set(items.map((item) => item.value)));
    if (!uids.length) return items;
    return getPlatformUsers({
      searchQuery: {
        conditions: [
          {
            field: nameOf<PlatformUser>("uid"),
            operation: QueryOperation.In,
            values: uids,
          },
        ],
      },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    })
      .then((users) => {
        const byUid = new Map(users.data.map((u) => [u.uid, u]));
        return items.map((item) => {
          const user = byUid.get(item.value);
          return user
            ? {
                ...item,
                display: fullName(user),
                dataItem: {
                  ...item.dataItem,
                  __name: user.departmentName,
                  __roleName: user.roleName,
                },
              }
            : item;
        });
      })
      .catch(() => items);
  });
}

function EventParticipantsPanel(props: EventParticipantsPanelProps) {
  const { eventUid } = props;
  const [messageApi, contextHolder] = message.useMessage();

  const [search, setSearch] = useState("");
  const [participants, setParticipants] = useState<ParticipantItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0); // internal refetch trigger (after a remove)

  // All participants of a saved event (newest first, so immediate adds surface at the top).
  useEffect(() => {
    if (!eventUid) return;
    setLoading(true);
    loadParticipants(eventUid)
      .then(setParticipants)
      .catch((e: any) =>
        messageApi.error(getProblemMessage(e, "Couldn't load participants.")),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUid, props.refreshKey, version]);

  const hostCount = participants.filter(
    (item) => participantRole(item) === EventParticipantRole.Host,
  ).length;

  // Search by name (full name or username) or role (role name, or Host/Attendee).
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return participants;
    return participants.filter((item) =>
      [item.display, item.dataItem?.__roleName, participantRole(item)].some(
        (field) => field?.toString().toLowerCase().includes(term),
      ),
    );
  }, [participants, search]);

  function onRemovePersisted(item: ParticipantItem): void {
    if (!item.dataItem?.uid) return;
    setLoading(true);
    deleteCalendarInvitation(item.dataItem.uid)
      .then(() => setVersion((v) => v + 1))
      .catch((e: any) => {
        setLoading(false);
        // Surface the server's reason (e.g. the last-host guard); fall back to a generic message.
        messageApi.error(
          getProblemMessage(e, "Couldn't remove this participant."),
        );
      });
  }

  // The final host can't be removed in isolation (the event would be left hostless) — the server enforces
  // this too; here we just disable the control with a hint.
  function removeDisabledReason(item: ParticipantItem): string | undefined {
    return participantRole(item) === EventParticipantRole.Host && hostCount <= 1
      ? "An event must keep at least one host — add another host first, or cancel the event."
      : undefined;
  }

  // One table for both modes: name with the department under it, the role-name tag (colored by event
  // role: gold host / blue attendee), and an optional remove control.
  function renderTable(opts: {
    data: ParticipantItem[];
    total: number;
    visibleRows: number;
    emptyText: string;
    onRemove?: (item: ParticipantItem, index: number) => void;
  }) {
    return (
      <TargetListFrame rowHeight={ROW_HEIGHT} visibleRows={opts.visibleRows}>
        <Table<ParticipantItem>
          sticky
          size="small"
          loading={loading && !!eventUid}
          rowKey={(item) => `${item.dataItem?.__type}|${item.value}`}
          pagination={false}
          dataSource={opts.data}
          locale={{
            emptyText: frameEmptyText(
              opts.emptyText,
              ROW_HEIGHT,
              opts.visibleRows,
            ),
          }}
          columns={[
            {
              title: `Attendee${opts.total ? ` (${opts.total})` : ""}`,
              key: "attendee",
              ellipsis: true,
              render: (_, item) => (
                // Fixed line heights (20px + 16px) keep every row exactly ROW_HEIGHT tall.
                <div className="flex min-w-0 flex-col">
                  <Typography.Text ellipsis className="block leading-5">
                    {item.display}
                  </Typography.Text>
                  <Typography.Text
                    ellipsis
                    type="secondary"
                    className="block h-4 text-xs leading-4"
                  >
                    {item.dataItem?.__name}
                  </Typography.Text>
                </div>
              ),
            },
            {
              title: "Role",
              key: "role",
              width: 170,
              // Blue: the user's role name (Employee, Manager, …), or "Attendee" when unknown.
              // Gold: an extra "Host" tag for the event's hosts.
              render: (_, item) => {
                const isHost = participantRole(item) === EventParticipantRole.Host;
                const roleName =
                  item.dataItem?.__roleName || (isHost ? undefined : EventParticipantRole.Attendee);
                return (
                  <div className="flex min-w-0 flex-nowrap items-center gap-1">
                    {roleName && (
                      <Tag color="blue" className="m-0 min-w-0 truncate">
                        {roleName}
                      </Tag>
                    )}
                    {isHost && (
                      <Tag
                        color={participantTagColor(EventParticipantRole.Host)}
                        className="m-0 shrink-0"
                      >
                        {EventParticipantRole.Host}
                      </Tag>
                    )}
                  </div>
                );
              },
            },
            ...(opts.onRemove
              ? [
                  {
                    key: "remove",
                    width: 44,
                    align: "center" as const,
                    render: (_: any, item: ParticipantItem, index: number) => {
                      const reason = eventUid
                        ? removeDisabledReason(item)
                        : undefined;
                      const button = (
                        <Button
                          type="text"
                          size="small"
                          disabled={!!reason}
                          aria-label={`Remove ${item.display}`}
                          icon={
                            <Icon
                              className="text-lg"
                              component={Icons.CloseIcon}
                            />
                          }
                          onClick={() => opts.onRemove!(item, index)}
                        />
                      );
                      return reason ? (
                        <Tooltip title={reason}>{button}</Tooltip>
                      ) : (
                        button
                      );
                    },
                  },
                ]
              : []),
          ]}
        />
      </TargetListFrame>
    );
  }

  // New event: the in-memory staged list (three rows visible before it scrolls).
  if (!eventUid) {
    return (
      <div className="mt-1">
        {renderTable({
          data: props.staged,
          total: props.staged.length,
          visibleRows: STAGED_VISIBLE_ROWS,
          emptyText: "No attendees added",
          onRemove: props.editable
            ? (_, index) => props.onRemoveStaged(index)
            : undefined,
        })}
      </div>
    );
  }

  // Saved event (preview / edit): all participants, searchable by name or role, four rows visible.
  return (
    <div className="mt-1 flex flex-col gap-2">
      {contextHolder}
      {/* Search is for reviewing (preview); while editing, the picker above is the way to find people. */}
      {!props.editable && (
        <Input
          allowClear
          placeholder="Search by name or role"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      {renderTable({
        data: props.editable ? participants : filtered,
        total: participants.length,
        visibleRows: PERSISTED_VISIBLE_ROWS,
        emptyText: !props.editable && search.trim() ? "No matches" : "No participants yet",
        onRemove: props.editable
          ? (item) => onRemovePersisted(item)
          : undefined,
      })}
    </div>
  );
}

export default EventParticipantsPanel;
